/**
 * Aufträge, Einsätze, Auftragszeiten und Material.
 *
 * **Die Zustandsbreite ist Absicht, nicht Fülle.** `EINSATZ_ZUSTAND` in
 * `packages/sync` kennt acht Zustände, und jeder davon kommt hier mindestens
 * einmal vor — einschließlich `storniert` und einschließlich des Rückwegs
 * `uebermittelt → abschluss`, den ADR-045 dem Büro einräumt. Ein Demostand,
 * in dem alle Einsätze `geplant` sind, prüft die Oberfläche gegen einen
 * Bruchteil dessen, was sie darstellen muss; die Farbe für `wartet` fällt
 * dann erst im Echtbetrieb als unlesbar auf.
 *
 * Ebenso Absicht: ein storniertes Einsatzobjekt, an dem **bereits Zeiten
 * hängen**. ADR-045 nimmt diesen Fall ausdrücklich in Kauf und `09` §9.3 führt
 * ihn als offene Frage an `EXP-001` — was bei der Übergabe an die
 * Branchensoftware mit solchen Posten geschieht, ist nicht entschieden. Wer
 * den Fall im Demostand sieht, kann darüber entscheiden; wer ihn nicht sieht,
 * entscheidet ihn versehentlich mit der ersten Implementierung.
 */

import type { Auftrag, Auftragszeit, Einsatz, Materialposten } from "./modell.js";
import { AUFTRAG, EINSATZ, KUNDE, PERSON, WARTUNGSPLAN } from "./kennungen.js";
import { ref } from "./modell.js";
import { tag, werktag, zeitpunkt } from "./zeit.js";

export function auftraege(bezugstag: Date): readonly Auftrag[] {
  return [
    {
      ref: AUFTRAG.kuehlung,
      nummer: "2481",
      titel: "Kühlaggregat Backstube – Störung E9",
      kunde: KUNDE.baeckerei,
      status: "in-arbeit",
      verantwortlich: PERSON.buero,
      angelegt: tag(bezugstag, -9),
      beschreibung:
        "Aggregat der Backstubenkühlung schaltet sprunghaft ab, Display zeigt E9. "
        + "Erster Einsatz brachte keine dauerhafte Abhilfe, Fehler trat erneut auf.",
      gewerk: "Kälte-Elektrik",
    },
    {
      ref: AUFTRAG.treppenhaus,
      nummer: "2483",
      titel: "Treppenhausbeleuchtung Luitpoldstraße 48 auf LED",
      kunde: KUNDE.hausverwaltung,
      status: "in-arbeit",
      verantwortlich: PERSON.buero,
      angelegt: tag(bezugstag, -21),
      beschreibung: "Vier Geschosse, Austausch inkl. Bewegungsmelder und Notlicht-Prüfung.",
      gewerk: "Beleuchtung",
      einsatzort: { strasse: "Luitpoldstraße 48", plz: "96052", ort: "Bamberg" },
    },
    {
      ref: AUFTRAG.hebebuehne,
      nummer: "2486",
      titel: "Hebebühne 2 – Jahresprüfung Elektrik",
      kunde: KUNDE.autohaus,
      status: "in-arbeit",
      verantwortlich: PERSON.inhaber,
      angelegt: tag(bezugstag, -30),
      gewerk: "Prüfung",
      wartungsplan: WARTUNGSPLAN.autohausHebebuehne,
    },
    {
      ref: AUFTRAG.wallbox,
      nummer: "2490",
      titel: "Wallbox 11 kW inkl. Zählerschrankerweiterung",
      kunde: KUNDE.privat,
      status: "in-arbeit",
      verantwortlich: PERSON.buero,
      angelegt: tag(bezugstag, -14),
      beschreibung:
        "Wallbox an der Garagenwand, Zuleitung 12 m, Netzbetreiberanmeldung liegt vor.",
      gewerk: "Installation",
    },
    {
      ref: AUFTRAG.rufanlage,
      nummer: "2492",
      titel: "Rufanlage Station 2 – Wartung",
      kunde: KUNDE.seniorenheim,
      status: "geplant",
      verantwortlich: PERSON.inhaber,
      angelegt: tag(bezugstag, -6),
      gewerk: "Sicherheitstechnik",
      wartungsplan: WARTUNGSPLAN.seniorenheimRufanlage,
    },
    {
      ref: AUFTRAG.hallenbeleuchtung,
      nummer: "2495",
      titel: "Hallenbeleuchtung Schweißerei – Erneuerung",
      kunde: KUNDE.metallbau,
      status: "zugewiesen",
      verantwortlich: PERSON.buero,
      angelegt: tag(bezugstag, -11),
      beschreibung: "18 Hallenstrahler, Montage nur bei stillstehender Produktion.",
      gewerk: "Beleuchtung",
    },
    {
      ref: AUFTRAG.strassenbeleuchtung,
      nummer: "2497",
      titel: "Straßenbeleuchtung Ortsdurchfahrt – Mast 12 bis 19",
      kunde: KUNDE.gemeinde,
      status: "geplant",
      verantwortlich: PERSON.inhaber,
      angelegt: tag(bezugstag, -18),
      gewerk: "Außenanlage",
    },
    {
      ref: AUFTRAG.praxisumbau,
      nummer: "2499",
      titel: "Praxisumbau – Elektroinstallation Behandlungsraum 3",
      kunde: KUNDE.praxis,
      status: "geplant",
      verantwortlich: PERSON.buero,
      angelegt: tag(bezugstag, -8),
      beschreibung: "Neue Zuleitungen, Netzwerkdosen, Medizingeräteabsicherung.",
      gewerk: "Installation",
    },
    {
      ref: AUFTRAG.pruefung2026,
      nummer: "2501",
      titel: "Prüfung ortsveränderlicher Betriebsmittel 2026",
      kunde: KUNDE.gemeinde,
      status: "geplant",
      verantwortlich: PERSON.inhaber,
      angelegt: tag(bezugstag, -5),
      gewerk: "Prüfung",
      wartungsplan: WARTUNGSPLAN.gemeindePruefung,
    },
    {
      ref: AUFTRAG.netzwerk,
      nummer: "2503",
      titel: "Netzwerkverkabelung Verwaltungstrakt",
      kunde: KUNDE.seniorenheim,
      status: "abgeschlossen",
      verantwortlich: PERSON.buero,
      angelegt: tag(bezugstag, -45),
      gewerk: "Datentechnik",
    },
    {
      ref: AUFTRAG.stoerung,
      nummer: "2505",
      titel: "Notdienst – Stromausfall Verkaufsraum",
      kunde: KUNDE.baeckerei,
      status: "in-arbeit",
      verantwortlich: PERSON.buero,
      angelegt: tag(bezugstag, 0),
      beschreibung: "Anruf 06:40 Uhr. Filiale ohne Strom im vorderen Bereich.",
      gewerk: "Störungsdienst",
    },
    {
      ref: AUFTRAG.zaehlerschrank,
      nummer: "2507",
      titel: "Zählerschrank Altbau – Erneuerung nach Befund",
      kunde: KUNDE.hausverwaltung,
      status: "zugewiesen",
      verantwortlich: PERSON.buero,
      angelegt: tag(bezugstag, -3),
      gewerk: "Installation",
      einsatzort: { strasse: "Obere Sandstraße 17", plz: "96049", ort: "Bamberg" },
    },
  ];
}

/**
 * Die Einsätze.
 *
 * Der heutige Tag ist absichtlich **voll und uneinheitlich**: sechs Einsätze,
 * verteilt auf drei Monteure, in fünf verschiedenen Zuständen. So beantwortet
 * das Dashboard alle vier Fragen, die `07` §7.4 ihm stellt, mit echten Daten
 * statt mit leeren Listen — und die Tagesansicht der Monteur-App zeigt für
 * jeden der drei etwas anderes.
 */
export function einsaetze(bezugstag: Date): readonly Einsatz[] {
  const s = (id: string, text: string, erledigt: boolean, pflicht = false) => ({
    ref: ref("vikunja", "subtask", id),
    text,
    erledigt,
    pflicht,
    ...(erledigt ? { erledigtAm: zeitpunkt(bezugstag, 0, 9, 20) } : {}),
  });

  return [
    // ───────── heute ─────────
    {
      // Läuft gerade: Der Monteur ist vor Ort, zwei von vier Schritten sind
      // abgehakt. Das ist der Einsatz, an dem sich die Detailansicht der
      // Monteur-App messen lassen muss.
      ref: EINSATZ.kuehlungHeute,
      auftrag: AUFTRAG.kuehlung,
      titel: "Zweiter Anlauf: Steuerplatine tauschen",
      zustand: "vor-ort",
      monteur: PERSON.monteurA,
      von: zeitpunkt(bezugstag, 0, 8, 0),
      bis: zeitpunkt(bezugstag, 0, 11, 0),
      prioritaet: "hoch",
      hinweis: "Backstube läuft ab 03:00. Aggregat erst nach Rücksprache stromlos machen.",
      folgtAuf: EINSATZ.kuehlungVorwoche,
      tatsaechlichAngekommen: zeitpunkt(bezugstag, 0, 8, 12),
      schritte: [
        s("st-9001", "Anlage spannungsfrei schalten und sichern", true, true),
        s("st-9002", "Steuerplatine ausbauen und Typenschild fotografieren", true, true),
        s("st-9003", "Ersatzplatine einbauen und Parameter übernehmen", false, true),
        s("st-9004", "Probelauf 30 Minuten, Fehlerspeicher auslesen", false, true),
      ],
    },
    {
      ref: EINSATZ.treppenhausHeute,
      auftrag: AUFTRAG.treppenhaus,
      titel: "3. und 4. OG – Leuchten und Melder",
      zustand: "anfahrt",
      monteur: PERSON.monteurB,
      von: zeitpunkt(bezugstag, 0, 9, 0),
      bis: zeitpunkt(bezugstag, 0, 13, 0),
      prioritaet: "normal",
      schritte: [
        s("st-9010", "Leuchten 3. OG tauschen", false, true),
        s("st-9011", "Leuchten 4. OG tauschen", false, true),
        s("st-9012", "Bewegungsmelder einstellen", false, false),
        s("st-9013", "Notlicht-Funktionsprüfung", false, true),
      ],
    },
    {
      // Wartet auf Material. `07` §7.8 kennt den Zustand ausdrücklich für
      // „Material oder Rückfrage" — und das Dashboard fragt nach „wo fehlt
      // eine Rückmeldung". Ohne einen wartenden Einsatz beantwortet es die
      // Frage nie.
      ref: EINSATZ.hebebuehneHeute,
      auftrag: AUFTRAG.hebebuehne,
      titel: "Jahresprüfung – Messung und Protokoll",
      zustand: "wartet",
      monteur: PERSON.monteurB,
      von: zeitpunkt(bezugstag, 0, 14, 0),
      bis: zeitpunkt(bezugstag, 0, 16, 30),
      prioritaet: "normal",
      wartetAuf: "Werkstatt kann Bühne 2 erst ab 15:30 freimachen",
      tatsaechlichAngekommen: zeitpunkt(bezugstag, 0, 14, 5),
      schritte: [
        s("st-9020", "Sichtprüfung Zuleitung und Not-Aus", true, true),
        s("st-9021", "Schutzleiterwiderstand messen", false, true),
        s("st-9022", "Isolationsmessung", false, true),
        s("st-9023", "Prüfprotokoll ausfüllen", false, true),
      ],
    },
    {
      ref: EINSATZ.wallboxHeute,
      auftrag: AUFTRAG.wallbox,
      titel: "Zuleitung verlegen und Zählerschrank erweitern",
      zustand: "geplant",
      monteur: PERSON.monteurA,
      von: zeitpunkt(bezugstag, 0, 13, 0),
      bis: zeitpunkt(bezugstag, 0, 17, 0),
      prioritaet: "normal",
      hinweis: "Hund im Garten. Vor Betreten klingeln.",
      schritte: [
        s("st-9030", "Kabelweg abstimmen", false, false),
        s("st-9031", "Zuleitung NYM-J 5x6 verlegen", false, true),
        s("st-9032", "Zählerschrank erweitern, FI Typ B setzen", false, true),
      ],
    },
    {
      // Der Notdienst von heute früh, schon durch und übermittelt: Er wartet
      // auf die Prüfung durch das Büro. Damit hat das Portal etwas zu prüfen,
      // sobald man die Auftragsakte öffnet.
      ref: EINSATZ.stoerungHeute,
      auftrag: AUFTRAG.stoerung,
      titel: "Notdienst Verkaufsraum – Fehlersuche",
      zustand: "uebermittelt",
      monteur: PERSON.monteurA,
      von: zeitpunkt(bezugstag, 0, 6, 45),
      bis: zeitpunkt(bezugstag, 0, 7, 45),
      prioritaet: "notfall",
      tatsaechlichAngekommen: zeitpunkt(bezugstag, 0, 7, 2),
      tatsaechlichAbgeschlossen: zeitpunkt(bezugstag, 0, 7, 51),
      schritte: [
        s("st-9040", "Fehler eingrenzen", true, true),
        s("st-9041", "Defekten LS-Schalter tauschen", true, true),
        s("st-9042", "Funktion prüfen und Kunden einweisen", true, true),
      ],
    },
    {
      // Ohne Monteur: „nicht zugewiesen" ist ein eigener Anzeigefall und
      // taucht sonst nirgends auf.
      ref: EINSATZ.praxisHeute,
      auftrag: AUFTRAG.praxisumbau,
      titel: "Aufmaß Behandlungsraum 3",
      zustand: "geplant",
      von: zeitpunkt(bezugstag, 0, 15, 0),
      bis: zeitpunkt(bezugstag, 0, 16, 0),
      prioritaet: "normal",
      hinweis: "Noch kein Monteur zugewiesen – Praxis nur mittwochs nachmittags.",
      schritte: [s("st-9050", "Aufmaß nehmen und fotografieren", false, true)],
    },

    // ───────── morgen ─────────
    {
      ref: EINSATZ.rufanlageMorgen,
      auftrag: AUFTRAG.rufanlage,
      titel: "Wartung Rufanlage Station 2",
      zustand: "geplant",
      monteur: PERSON.monteurB,
      von: zeitpunkt(bezugstag, werktag(bezugstag, 1), 8, 0),
      bis: zeitpunkt(bezugstag, werktag(bezugstag, 1), 12, 0),
      prioritaet: "hoch",
      hinweis: "Ausfall über 30 Minuten der Heimleitung melden.",
      schritte: [
        s("st-9060", "Alle Rufknöpfe Station 2 auslösen und quittieren", false, true),
        s("st-9061", "Zentrale: Akkutest", false, true),
        s("st-9062", "Wartungsprotokoll ausfüllen", false, true),
      ],
    },
    {
      ref: EINSATZ.hallenbeleuchtungMorgen,
      auftrag: AUFTRAG.hallenbeleuchtung,
      titel: "Strahler 1 bis 9 montieren",
      zustand: "geplant",
      monteur: PERSON.monteurA,
      von: zeitpunkt(bezugstag, werktag(bezugstag, 1), 7, 30),
      bis: zeitpunkt(bezugstag, werktag(bezugstag, 1), 15, 30),
      prioritaet: "normal",
      hinweis: "Nur bei stillstehender Produktion. Hubarbeitsbühne ist bestellt.",
      schritte: [
        s("st-9070", "Hubarbeitsbühne einweisen lassen", false, true),
        s("st-9071", "Altstrahler demontieren", false, false),
        s("st-9072", "Neue Strahler montieren und anschließen", false, true),
      ],
    },
    {
      ref: EINSATZ.wallboxMorgen,
      auftrag: AUFTRAG.wallbox,
      titel: "Wallbox setzen, anschließen und in Betrieb nehmen",
      zustand: "geplant",
      monteur: PERSON.monteurA,
      von: zeitpunkt(bezugstag, werktag(bezugstag, 1), 16, 0),
      bis: zeitpunkt(bezugstag, werktag(bezugstag, 1), 18, 0),
      prioritaet: "normal",
      folgtAuf: EINSATZ.wallboxHeute,
      schritte: [
        s("st-9080", "Wallbox montieren und anschließen", false, true),
        s("st-9081", "Inbetriebnahme und Messprotokoll", false, true),
        s("st-9082", "Kunden einweisen", false, false),
      ],
    },
    {
      ref: EINSATZ.zaehlerschrankMorgen,
      auftrag: AUFTRAG.zaehlerschrank,
      titel: "Bestandsaufnahme Zählerschrank",
      zustand: "geplant",
      monteur: PERSON.monteurB,
      von: zeitpunkt(bezugstag, werktag(bezugstag, 1), 13, 0),
      bis: zeitpunkt(bezugstag, werktag(bezugstag, 1), 15, 0),
      prioritaet: "normal",
      schritte: [s("st-9090", "Bestand aufnehmen und fotografieren", false, true)],
    },

    // ───────── später ─────────
    {
      ref: EINSATZ.strassenbeleuchtungSpaeter,
      auftrag: AUFTRAG.strassenbeleuchtung,
      titel: "Mast 12 bis 15 – Leuchtenköpfe tauschen",
      zustand: "geplant",
      monteur: PERSON.monteurB,
      von: zeitpunkt(bezugstag, werktag(bezugstag, 4), 8, 0),
      bis: zeitpunkt(bezugstag, werktag(bezugstag, 4), 16, 0),
      prioritaet: "normal",
      hinweis: "Halbseitige Sperrung ist angemeldet.",
      schritte: [s("st-9100", "Leuchtenköpfe Mast 12–15 tauschen", false, true)],
    },
    {
      ref: EINSATZ.praxisumbauSpaeter,
      auftrag: AUFTRAG.praxisumbau,
      titel: "Installation Behandlungsraum 3",
      zustand: "geplant",
      monteur: PERSON.monteurA,
      von: zeitpunkt(bezugstag, werktag(bezugstag, 7), 13, 0),
      bis: zeitpunkt(bezugstag, werktag(bezugstag, 7), 18, 0),
      prioritaet: "normal",
      schritte: [
        s("st-9110", "Zuleitungen ziehen", false, true),
        s("st-9111", "Netzwerkdosen setzen", false, true),
        s("st-9112", "Medizingeräteabsicherung setzen und messen", false, true),
      ],
    },
    {
      ref: EINSATZ.pruefungSpaeter,
      auftrag: AUFTRAG.pruefung2026,
      titel: "Prüfung Rathaus und Bauhof",
      zustand: "geplant",
      monteur: PERSON.monteurC,
      von: zeitpunkt(bezugstag, werktag(bezugstag, 9), 8, 0),
      bis: zeitpunkt(bezugstag, werktag(bezugstag, 9), 17, 0),
      prioritaet: "niedrig",
      hinweis: "Zugewiesener Monteur ist derzeit krankgemeldet – Zuweisung prüfen.",
      schritte: [s("st-9120", "Geräteliste abarbeiten", false, true)],
    },
    {
      ref: EINSATZ.netzwerkSpaeter,
      auftrag: AUFTRAG.netzwerk,
      titel: "Nachbesserung Patchfeld",
      zustand: "geplant",
      monteur: PERSON.monteurB,
      von: zeitpunkt(bezugstag, werktag(bezugstag, 11), 9, 0),
      bis: zeitpunkt(bezugstag, werktag(bezugstag, 11), 11, 0),
      prioritaet: "niedrig",
      folgtAuf: EINSATZ.netzwerkVorwoche,
      schritte: [s("st-9130", "Patchfeld beschriften", false, false)],
    },

    // ───────── abgeschlossen und geprüft ─────────
    {
      ref: EINSATZ.kuehlungVorwoche,
      auftrag: AUFTRAG.kuehlung,
      titel: "Erste Fehlersuche Kühlaggregat",
      zustand: "geprueft",
      monteur: PERSON.monteurA,
      von: zeitpunkt(bezugstag, -7, 8, 0),
      bis: zeitpunkt(bezugstag, -7, 10, 30),
      prioritaet: "hoch",
      tatsaechlichAngekommen: zeitpunkt(bezugstag, -7, 8, 5),
      tatsaechlichAbgeschlossen: zeitpunkt(bezugstag, -7, 10, 40),
      schritte: [
        s("st-8901", "Fehlerspeicher auslesen", true, true),
        s("st-8902", "Fühler prüfen und reinigen", true, true),
      ],
    },
    {
      ref: EINSATZ.treppenhausVorwoche,
      auftrag: AUFTRAG.treppenhaus,
      titel: "EG bis 2. OG – Leuchten und Melder",
      zustand: "geprueft",
      monteur: PERSON.monteurB,
      von: zeitpunkt(bezugstag, -5, 9, 0),
      bis: zeitpunkt(bezugstag, -5, 15, 0),
      prioritaet: "normal",
      tatsaechlichAbgeschlossen: zeitpunkt(bezugstag, -5, 15, 20),
      schritte: [
        s("st-8910", "Leuchten EG bis 2. OG tauschen", true, true),
        s("st-8911", "Bewegungsmelder einstellen", true, false),
      ],
    },
    {
      // Vom Büro zurückgegeben: `uebermittelt → abschluss` nach ADR-045. Der
      // Fall gehört in den Bestand, weil er der einzige Rückweg im Automaten
      // ist und in der Oberfläche eine eigene Meldung braucht.
      ref: EINSATZ.hebebuehneVorwoche,
      auftrag: AUFTRAG.hebebuehne,
      titel: "Hebebühne 1 – Jahresprüfung",
      zustand: "abschluss",
      monteur: PERSON.monteurB,
      von: zeitpunkt(bezugstag, -4, 14, 0),
      bis: zeitpunkt(bezugstag, -4, 16, 0),
      prioritaet: "normal",
      hinweis: "Vom Büro zurückgegeben: Messwerte im Protokoll fehlen.",
      tatsaechlichAbgeschlossen: zeitpunkt(bezugstag, -4, 16, 10),
      schritte: [
        s("st-8920", "Sichtprüfung", true, true),
        s("st-8921", "Messwerte eintragen", false, true),
      ],
    },
    {
      ref: EINSATZ.rufanlageVorwoche,
      auftrag: AUFTRAG.rufanlage,
      titel: "Wartung Rufanlage Station 1",
      zustand: "geprueft",
      monteur: PERSON.monteurB,
      von: zeitpunkt(bezugstag, -12, 8, 0),
      bis: zeitpunkt(bezugstag, -12, 11, 0),
      prioritaet: "hoch",
      schritte: [s("st-8930", "Wartung durchführen", true, true)],
    },
    {
      ref: EINSATZ.netzwerkVorwoche,
      auftrag: AUFTRAG.netzwerk,
      titel: "Verkabelung Verwaltungstrakt",
      zustand: "geprueft",
      monteur: PERSON.monteurA,
      von: zeitpunkt(bezugstag, -20, 7, 30),
      bis: zeitpunkt(bezugstag, -20, 16, 0),
      prioritaet: "normal",
      schritte: [s("st-8940", "Kabel ziehen und auflegen", true, true)],
    },
    {
      // Storniert **mit bereits erfassten Zeiten**. ADR-045 nimmt das in Kauf,
      // `09` §9.3 führt die Folgefrage an `EXP-001`.
      ref: EINSATZ.hallenbeleuchtungStorniert,
      auftrag: AUFTRAG.hallenbeleuchtung,
      titel: "Strahler 1 bis 9 montieren (abgesagt)",
      zustand: "storniert",
      monteur: PERSON.monteurA,
      von: zeitpunkt(bezugstag, -2, 7, 30),
      bis: zeitpunkt(bezugstag, -2, 15, 30),
      prioritaet: "normal",
      hinweis: "Kunde hat morgens abgesagt, Produktion lief durch. Anfahrt war bereits erfolgt.",
      tatsaechlichAngekommen: zeitpunkt(bezugstag, -2, 7, 40),
      schritte: [s("st-8950", "Hubarbeitsbühne einweisen lassen", false, true)],
    },
  ];
}

/**
 * Auftragszeiten — nach `01` §1.12 die auf einen Einsatz gebuchte Zeit, nicht
 * die gesetzliche Arbeitszeit.
 *
 * Die Anfahrt ist eine eigene Position und keine Vorlaufzeit im Arbeitsblock.
 * Das ist der Grund, aus dem ADR-045 den Rücksprung `uebermittelt → vor-ort`
 * verbietet: Bei zwei Vor-Ort-Blöcken mit Lücke wäre nicht mehr entscheidbar,
 * welche Anfahrt zu welcher Arbeit gehört — und die Positionen gehen nach
 * `EXP-001` in die Abrechnung.
 */
export function zeiten(bezugstag: Date): readonly Auftragszeit[] {
  const z = (
    id: string,
    einsatz: Auftragszeit["einsatz"],
    monteur: Auftragszeit["monteur"],
    art: Auftragszeit["art"],
    tageVersatz: number,
    vonStunde: number,
    vonMinute: number,
    bisStunde: number,
    bisMinute: number,
    abrechenbar = true,
    notiz?: string,
  ): Auftragszeit => ({
    ref: ref("portal", "worktime", id),
    einsatz,
    monteur,
    art,
    von: zeitpunkt(bezugstag, tageVersatz, vonStunde, vonMinute),
    bis: zeitpunkt(bezugstag, tageVersatz, bisStunde, bisMinute),
    abrechenbar,
    ...(notiz ? { notiz } : {}),
  });

  return [
    // heute, laufender Einsatz
    z("zt-7001", EINSATZ.kuehlungHeute, PERSON.monteurA, "anfahrt", 0, 7, 45, 8, 12),
    z("zt-7002", EINSATZ.kuehlungHeute, PERSON.monteurA, "arbeit", 0, 8, 12, 10, 30),
    // Notdienst heute früh
    z("zt-7003", EINSATZ.stoerungHeute, PERSON.monteurA, "anfahrt", 0, 6, 45, 7, 2),
    z("zt-7004", EINSATZ.stoerungHeute, PERSON.monteurA, "arbeit", 0, 7, 2, 7, 51,
      true, "Notdienstzuschlag außerhalb der Regelarbeitszeit"),
    z("zt-7005", EINSATZ.stoerungHeute, PERSON.monteurA, "rueckfahrt", 0, 7, 51, 8, 5,
      false, "Rückfahrt fiel mit der Anfahrt zum Folgeeinsatz zusammen"),
    // heute nachmittag, wartend
    z("zt-7006", EINSATZ.hebebuehneHeute, PERSON.monteurB, "anfahrt", 0, 13, 40, 14, 5),
    z("zt-7007", EINSATZ.hebebuehneHeute, PERSON.monteurB, "arbeit", 0, 14, 5, 14, 40),
    // vergangene Einsätze
    z("zt-6901", EINSATZ.kuehlungVorwoche, PERSON.monteurA, "anfahrt", -7, 7, 40, 8, 5),
    z("zt-6902", EINSATZ.kuehlungVorwoche, PERSON.monteurA, "arbeit", -7, 8, 5, 10, 40),
    z("zt-6903", EINSATZ.treppenhausVorwoche, PERSON.monteurB, "anfahrt", -5, 8, 35, 9, 0),
    z("zt-6904", EINSATZ.treppenhausVorwoche, PERSON.monteurB, "arbeit", -5, 9, 0, 15, 20),
    z("zt-6905", EINSATZ.hebebuehneVorwoche, PERSON.monteurB, "arbeit", -4, 14, 0, 16, 10),
    z("zt-6906", EINSATZ.rufanlageVorwoche, PERSON.monteurB, "arbeit", -12, 8, 0, 11, 0),
    z("zt-6907", EINSATZ.netzwerkVorwoche, PERSON.monteurA, "arbeit", -20, 7, 30, 16, 0),
    // Der stornierte Einsatz — die Anfahrt war schon gefahren.
    z("zt-6908", EINSATZ.hallenbeleuchtungStorniert, PERSON.monteurA, "anfahrt",
      -2, 7, 0, 7, 40, true, "Anfahrt erfolgt, Einsatz vor Ort abgesagt"),
  ];
}

export function material(bezugstag: Date): readonly Materialposten[] {
  const m = (
    id: string,
    einsatz: Materialposten["einsatz"],
    bezeichnung: string,
    menge: number,
    einheit: string,
    tageVersatz: number,
    erfasstVon: Materialposten["erfasstVon"],
    artikelnummer?: string,
    abrechenbar = true,
    bestellnummer?: string,
  ): Materialposten => ({
    ref: ref("portal", "material", id),
    einsatz,
    bezeichnung,
    menge,
    einheit,
    abrechenbar,
    erfasstVon,
    erfasstAm: zeitpunkt(bezugstag, tageVersatz, 10, 0),
    ...(artikelnummer ? { artikelnummer } : {}),
    ...(bestellnummer ? { bestellnummer } : {}),
  });

  return [
    m("mt-5001", EINSATZ.kuehlungHeute, "Steuerplatine Kälteregler KR-40", 1, "Stück",
      0, PERSON.monteurA, "KR40-STP", true, "88109"),
    m("mt-5002", EINSATZ.kuehlungHeute, "Wärmeleitpaste", 1, "Tube", 0, PERSON.monteurA),
    m("mt-5003", EINSATZ.stoerungHeute, "Leitungsschutzschalter B16, 1-polig", 2, "Stück",
      0, PERSON.monteurA, "LS-B16-1"),
    m("mt-5004", EINSATZ.hebebuehneHeute, "Prüfplakette 2026", 4, "Stück",
      0, PERSON.monteurB, undefined, false),
    m("mt-5005", EINSATZ.treppenhausVorwoche, "LED-Wannenleuchte 1200 mm, 30 W", 9, "Stück",
      -5, PERSON.monteurB, "LED-WL-1200", true, "87944"),
    m("mt-5006", EINSATZ.treppenhausVorwoche, "Bewegungsmelder 180°, IP20", 3, "Stück",
      -5, PERSON.monteurB, "BM180-IP20"),
    m("mt-5007", EINSATZ.kuehlungVorwoche, "Temperaturfühler PT1000", 1, "Stück",
      -7, PERSON.monteurA, "PT1000-K"),
    m("mt-5008", EINSATZ.netzwerkVorwoche, "Verlegekabel Cat.7 S/FTP", 320, "Meter",
      -20, PERSON.monteurA, "CAT7-VK", true, "87610"),
    m("mt-5009", EINSATZ.netzwerkVorwoche, "Netzwerkdose Cat.6A, 2-fach", 14, "Stück",
      -20, PERSON.monteurA, "ND-6A-2"),
    m("mt-5010", EINSATZ.rufanlageVorwoche, "Akku 12 V 7 Ah für Rufanlagenzentrale", 2, "Stück",
      -12, PERSON.monteurB, "AKK-12-7", true, "87301"),
    /*
     * Das Material zum Wallbox-Auftrag — der Fall, den die Lieferanten-
     * rechnung im Eingangskorb meint. Die Bestellnummer 88214 steht auf
     * ihrer Betreffzeile; ohne den Posten hier hätte der Vorschlag dort
     * nichts, woran er sich festmachen könnte.
     */
    m("mt-5011", EINSATZ.wallboxHeute, "Wallbox 11 kW, Typ 2, mit Lastmanagement", 1, "Stück",
      0, PERSON.monteurA, "WB-11-T2", true, "88214"),
    m("mt-5012", EINSATZ.wallboxHeute, "Leitung NYM-J 5x6", 14, "Meter",
      0, PERSON.monteurA, "NYMJ-5X6", true, "88214"),
  ];
}
